import { uid } from '../../../../lib/uid';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import type { EligibilityOption } from '../../eligibilities/eligibilityTypes';

/**
 * Test data for the eligibility form's Autogenerate.
 *
 * **The chosen eligibility decides the rest of the form**, so the generator picks one from the
 * catalog the modal already loaded and then fills what that choice makes required: an
 * eligibility carrying `entailsLicense` gets a licence number and a validity date, one that
 * does not gets neither. Generating a licence number for an eligibility that entails no
 * licence would fill a field the form does not draw.
 */
export interface SampleEligibility {
  eligibilityId: string | null;
  rating: string;
  dateOfExamination: string;
  placeOfExamination: string;
  /** Empty unless the chosen eligibility entails a licence. */
  licenseNumber: string;
  licenseValidUntil: string;
}

export function sampleEligibility(options: readonly EligibilityOption[]): SampleEligibility {
  const tag = uid().slice(0, 6).toUpperCase();

  /*
    A real row from the loaded catalog. An invented id fails the server's "it must exist and be
    active" check, and that failure reads as a bug in the form rather than in the generator.
    An empty catalog yields null — a module with nothing configured, not a value to invent.
  */
  const chosen = options.length === 0 ? null : options[Math.floor(Math.random() * options.length)];

  /*
    Calendar dates built from parts, never `toISOString` — that converts through UTC and can
    hand the field the day before the one intended.
  */
  const today = todayCalendarDate();
  const [year, month, day] = today.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');

  // Examined a few years ago, which every rule on this form accepts: not in the future, and
  // before any licence expiry below.
  const examYear = year - 2 - Math.floor(Math.random() * 6);

  return {
    eligibilityId: chosen?.id ?? null,

    // CS Form 212 ratings are percentages. A pass, so the value reads as a real result.
    rating: String(80 + Math.floor(Math.random() * 20)),
    dateOfExamination: `${examYear}-${pad(month)}-${pad(day)}`,

    // Obviously synthetic on sight, like every other generated value in this module.
    placeOfExamination: 'Test City',

    // Only where the catalog says this eligibility carries one — otherwise the form draws no
    // licence fields and these would be values nothing on screen accounts for.
    licenseNumber: chosen?.entailsLicense ? `LIC-${tag}` : '',
    licenseValidUntil: chosen?.entailsLicense ? `${year + 3}-${pad(month)}-${pad(day)}` : '',
  };
}
